import { useShopI18n } from '../../i18n'
import type { CheckoutSavedAddress } from '../addressBookAdapter'

type Props = {
  groupLabel: string
  addresses: CheckoutSavedAddress[]
  selectedAddressId: string | null
  disabled: boolean
  onSelectAddress: (address: CheckoutSavedAddress) => void
  onEditAddress: (address: CheckoutSavedAddress) => void
  onAddNewAddress: () => void
}

const formatAddressLine = (address: CheckoutSavedAddress) => {
  const cityRow = [address.address.postalCode, address.address.city].filter(Boolean).join(' ').trim()
  return [address.address.line1, cityRow, address.address.country].filter(Boolean).join(', ')
}

export const SavedAddressCards = ({
  groupLabel,
  addresses,
  selectedAddressId,
  disabled,
  onSelectAddress,
  onEditAddress,
  onAddNewAddress,
}: Props) => {
  const { t } = useShopI18n()

  if (addresses.length === 0) return null

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{t('checkout.addressBook.title')}</p>
        <button
          type="button"
          className="text-xs font-semibold text-slate-700 underline underline-offset-2"
          onClick={onAddNewAddress}
          disabled={disabled}
        >
          {t('checkout.addressBook.addNew')}
        </button>
      </div>

      <div className="grid gap-2 md:grid-cols-2" role="radiogroup" aria-label={groupLabel}>
        {addresses.map((address) => {
          const isSelected = selectedAddressId === address.id
          return (
            <div
              key={address.id}
              role="radio"
              tabIndex={disabled ? -1 : isSelected ? 0 : -1}
              aria-checked={isSelected}
              onClick={() => {
                if (disabled) return
                onSelectAddress(address)
              }}
              onKeyDown={(event) => {
                if (disabled) return
                if (event.key !== ' ' && event.key !== 'Enter') return
                event.preventDefault()
                onSelectAddress(address)
              }}
              className={`rounded-xl border px-3 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                isSelected
                  ? 'border-emerald-400 bg-emerald-50'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className={`inline-flex h-4 w-4 items-center justify-center rounded-full border ${
                      isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 bg-white'
                    }`}
                  >
                    {isSelected ? <span className="h-1.5 w-1.5 rounded-full bg-white" /> : null}
                  </span>
                  <p className="text-sm font-semibold text-slate-900">
                    {address.address.firstName} {address.address.lastName}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {isSelected ? (
                    <span className="rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                      {t('checkout.addressBook.selectedBadge')}
                    </span>
                  ) : null}
                  {address.defaultShipping || address.defaultBilling ? (
                    <span className="rounded-full border border-slate-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                      {t('checkout.addressBook.defaultBadge')}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className="rounded-md border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
                    onClick={(event) => {
                      event.stopPropagation()
                      onEditAddress(address)
                    }}
                    disabled={disabled}
                  >
                    {t('checkout.cta.edit')}
                  </button>
                </div>
              </div>
              <p className="mt-1 text-xs text-slate-600">{formatAddressLine(address)}</p>
              {address.address.phone ? <p className="mt-1 text-xs text-slate-500">{address.address.phone}</p> : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}
